/**
 * Email Integration Panel
 * Universal email management for all case types
 * Displays case-related emails, allows replies, and email actions
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  IconButton,
  Avatar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Divider,
  Badge,
  Paper,
  Tooltip,
  Stack,
  FormControlLabel,
  Switch,
  LinearProgress,
  CircularProgress,
  Fab,
  Collapse,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Autocomplete
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Reply as ReplyIcon,
  Refresh as RefreshIcon,
  Drafts as DraftsIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Phone as PhoneIcon,
  AccessTime as AccessTimeIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useNotification } from '../../../contexts/NotificationContext';
import { caseManagementService } from '../../../services/caseManagementService';
import emailService, { EmailData as EmailServiceData } from '../../../services/emailService';
import EmailDetail from '../../email/EmailDetail';
import { styled } from '@mui/material/styles';

// Styled components following design framework
const EmailCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  borderLeft: `4px solid ${theme.palette.primary.main}`,
  transition: theme.transitions.create(['box-shadow', 'transform'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    boxShadow: theme.shadows[4],
    transform: 'translateY(-2px)'
  }
}));

const UnreadEmailCard = styled(EmailCard)(({ theme }) => ({
  borderLeft: `4px solid ${theme.palette.warning.main}`,
  backgroundColor: theme.palette.warning.light + '10'
}));

interface EmailData {
  id: string;
  message_id: string;
  thread_id?: string;
  from_address: string;
  to_addresses: string | string[];
  cc_addresses?: string;
  bcc_addresses?: string;
  reply_to?: string;
  subject: string;
  body_text: string;
  body_html?: string;
  direction: 'inbound' | 'outbound' | 'draft';
  email_date: string;
  received_date?: string;
  sent_date?: string;
  mailjet_message_id?: string;
  status: 'unread' | 'read' | 'archived' | 'deleted' | 'draft' | 'sent';
  is_starred?: boolean;
  folder?: string;
  linked_entity_type?: string;
  linked_entity_id?: string;
  created_at: string;
  updated_at: string;
  attachments?: any[];
  labels?: any[];
}

interface EmailIntegrationPanelProps {
  caseId: string;
  caseType: string;
  customerEmail?: string;
  selectedCase?: any; // Full case object for enhanced functionality
  onEmailSent?: () => void;
}

// Enhanced interfaces for integrated functionality
interface CustomerDecision {
  decision: 'won' | 'lost' | null;
  decision_notes: string;
  customer_feedback: string;
  decision_amount: string;
  expected_order_date: string;
  decided_by: string;
  decision_date?: string;
}

interface CustomerResponse {
  response_id: string;
  response_type: string;
  response_content: string;
  contact_method: string;
  recorded_at: string;
  recorded_by: string;
  next_action?: string;
}

interface EmailStatus {
  email_sent: boolean;
  recipient?: string;
  sent_at?: string;
  language?: string;
  delivery_status?: string;
  pdf_attached?: string;
}

export const EmailIntegrationPanel: React.FC<EmailIntegrationPanelProps> = ({
  caseId,
  caseType,
  customerEmail,
  selectedCase,
  onEmailSent
}) => {
  const { showNotification } = useNotification();
  
  // State management
  const [emails, setEmails] = useState<EmailData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [sendingEmail, setSendingEmail] = useState<boolean>(false);
  const [composeDialogOpen, setComposeDialogOpen] = useState<boolean>(false);
  const [replyDialogOpen, setReplyDialogOpen] = useState<boolean>(false);
  const [emailDetailDialogOpen, setEmailDetailDialogOpen] = useState<boolean>(false);
  const [selectedEmail, setSelectedEmail] = useState<EmailData | null>(null);
  
  // Email composition state
  const [newEmail, setNewEmail] = useState({
    to: customerEmail || '',
    subject: '',
    body: '',
    cc: '',
    bcc: ''
  });
  
  // Reply composition state
  const [replyEmail, setReplyEmail] = useState({
    subject: '',
    body: ''
  });
  
  // Enhanced state for Customer Decision Panel
  const [customerDecision, setCustomerDecision] = useState<CustomerDecision>({
    decision: null,
    decision_notes: '',
    customer_feedback: '',
    decision_amount: '',
    expected_order_date: '',
    decided_by: 'user@wzf.com'
  });
  const [customerResponses, setCustomerResponses] = useState<CustomerResponse[]>([]);
  const [emailStatus, setEmailStatus] = useState<EmailStatus | null>(null);
  const [decisionDialogOpen, setDecisionDialogOpen] = useState<'won' | 'lost' | null>(null);
  
  // Enhanced state for Supplier Quote Selection Review Panel
  const [supplierQuoteSelections, setSupplierQuoteSelections] = useState<any[]>([]);
  const [loadingQuoteSelections, setLoadingQuoteSelections] = useState(false);
  
  // Response recording form
  const [responseForm, setResponseForm] = useState({
    response_type: 'inquiry',
    response_content: '',
    contact_method: 'email',
    next_action: '',
    recorded_by: 'user@wzf.com'
  });

  useEffect(() => {
    loadCaseEmails();
    loadEmailStatus();
    loadQuoteSelections();
    // Load customer decision data if this is a customer case
    if (caseType === 'customer_order') {
      loadCustomerDecision();
    }
  }, [caseId, caseType]);

  const handleEmailUpdate = async (emailId: string, updates: Partial<EmailServiceData>) => {
    console.group('🔧 EmailIntegrationPanel - handleEmailUpdate');
    console.log('📧 Email ID:', emailId);
    console.log('📝 Updates:', updates);
    
    try {
      console.log('🌐 Calling emailService.updateEmail...');
      const result = await emailService.updateEmail(emailId, updates);
      console.log('✅ emailService.updateEmail result:', result);
      
      console.log('🔄 Reloading case emails...');
      await loadCaseEmails();
      console.log('✅ Case emails reloaded successfully');
      
      showNotification('E-Mail erfolgreich aktualisiert', 'success');
      console.log('✅ Email updated successfully');
    } catch (error: any) {
      console.error('❌ Error updating email:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      showNotification('Fehler beim Aktualisieren der E-Mail', 'error');
    }
    console.groupEnd();
  };

  const loadCaseEmails = async () => {
    if (!caseId) {
      console.warn('⚠️ loadCaseEmails called without caseId');
      return;
    }
    
    console.group('📬 EmailIntegrationPanel - loadCaseEmails');
    console.log('📋 Case ID:', caseId);
    
    setLoading(true);
    try {
      console.log('🌐 Calling caseManagementService.getCaseEmails...');
      const response = await caseManagementService.getCaseEmails(caseId);
      console.log('📨 getCaseEmails response:', response);
      
      if (response.success && response.data) {
        console.log('✅ Emails loaded successfully:', response.data.length, 'emails');
        console.log('📧 Email details:', response.data.map(e => ({
          id: e.id,
          subject: e.subject,
          from: e.from_address,
          linked_entity_type: e.linked_entity_type,
          linked_entity_id: e.linked_entity_id
        })));
        setEmails(response.data);
      } else {
        console.error('❌ Failed to load emails:', response);
        showNotification('Fehler beim Laden der E-Mails', 'error');
      }
    } catch (error: any) {
      console.error('❌ Error loading case emails:', error);
      console.error('❌ Error details:', {
        message: error.message,
        status: error.status,
        response: error.response
      });
      showNotification('Fehler beim Laden der E-Mails: ' + error.message, 'error');
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  const loadEmailStatus = async () => {
    try {
      const response = await fetch(`/api/v3/customer-quotes/${caseId}/email-status`);
      const data = await response.json();
      
      if (data.success) {
        setEmailStatus(data.data);
      }
    } catch (err) {
      console.warn('Could not load email status:', err);
    }
  };

  const loadQuoteSelections = async () => {
    setLoadingQuoteSelections(true);
    try {
      // TODO: Implement API call to load supplier quote selections for this customer case
      // This should load the selected supplier quotes from the "Select & Compile" tab
      console.log('Loading quote selections for case:', caseId);
    } catch (error) {
      console.error('Failed to load quote selections:', error);
      showNotification('Fehler beim Laden der Angebotauswahl', 'error');
    } finally {
      setLoadingQuoteSelections(false);
    }
  };

  const loadCustomerDecision = async () => {
    try {
      const response = await fetch(`/api/v3/customer-quotes/${caseId}/decision`);
      const data = await response.json();
      
      if (data.success && data.data) {
        setCustomerDecision(data.data);
      }
    } catch (err) {
      console.warn('Could not load customer decision:', err);
    }
  };

  const handleRecordDecision = async (decision: 'won' | 'lost') => {
    try {
      const decisionData = {
        ...customerDecision,
        decision,
        decision_date: new Date().toISOString()
      };

      const response = await fetch(`/api/v3/customer-quotes/${caseId}/decision`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(decisionData)
      });

      const data = await response.json();
      if (data.success) {
        setCustomerDecision(decisionData);
        setDecisionDialogOpen(null);
        showNotification(`Fall als ${decision === 'won' ? 'gewonnen' : 'verloren'} markiert`, 'success');
      } else {
        showNotification('Fehler beim Speichern der Entscheidung', 'error');
      }
    } catch (error) {
      showNotification('Fehler beim Speichern der Entscheidung', 'error');
    }
  };

  const handleSendEmail = async () => {
    if (!newEmail.to.trim() || !newEmail.subject.trim() || !newEmail.body.trim()) {
      showNotification('Bitte füllen Sie alle Pflichtfelder aus', 'warning');
      return;
    }

    setSendingEmail(true);
    try {
      const emailData = {
        case_id: caseId,
        to: newEmail.to,
        subject: newEmail.subject,
        body: newEmail.body,
        cc: newEmail.cc || undefined,
        bcc: newEmail.bcc || undefined
      };

      const response = await caseManagementService.sendCaseEmail(caseId, emailData);
      
      if (response.success) {
        showNotification('E-Mail erfolgreich gesendet!', 'success');
        setComposeDialogOpen(false);
        setNewEmail({ to: customerEmail || '', subject: '', body: '', cc: '', bcc: '' });
        await loadCaseEmails();
        onEmailSent?.();
      } else {
        showNotification('Fehler beim Senden der E-Mail: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error sending email:', error);
      showNotification('Fehler beim Senden der E-Mail: ' + error.message, 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const handleReplyToEmail = async () => {
    if (!selectedEmail || !replyEmail.body.trim()) {
      showNotification('Bitte geben Sie eine Antwort ein', 'warning');
      return;
    }

    setSendingEmail(true);
    try {
      const replyData = {
        case_id: caseId,
        to: selectedEmail.from_address,
        subject: replyEmail.subject || `Re: ${selectedEmail.subject}`,
        body: replyEmail.body,
        in_reply_to: selectedEmail.message_id
      };

      const response = await caseManagementService.sendCaseEmail(caseId, replyData);
      
      if (response.success) {
        showNotification('Antwort erfolgreich gesendet!', 'success');
        setReplyDialogOpen(false);
        setReplyEmail({ subject: '', body: '' });
        setSelectedEmail(null);
        await loadCaseEmails();
        onEmailSent?.();
      } else {
        showNotification('Fehler beim Senden der Antwort: ' + response.message, 'error');
      }
    } catch (error: any) {
      console.error('Error sending reply:', error);
      showNotification('Fehler beim Senden der Antwort: ' + error.message, 'error');
    } finally {
      setSendingEmail(false);
    }
  };

  const getEmailTypeChip = (type: string, status: string) => {
    const typeConfig: { [key: string]: { label: string; color: any } } = {
      outbound: { label: 'Ausgehend', color: 'primary' as const },
      inbound: { label: 'Eingehend', color: 'secondary' as const },
      draft: { label: 'Entwurf', color: 'info' as const },
      // Legacy support
      outgoing: { label: 'Ausgehend', color: 'primary' as const },
      incoming: { label: 'Eingehend', color: 'secondary' as const },
      reply: { label: 'Antwort', color: 'info' as const }
    };

    const statusConfig: { [key: string]: { color: any } } = {
      sent: { color: 'success' as const },
      delivered: { color: 'success' as const },
      failed: { color: 'error' as const },
      pending: { color: 'warning' as const },
      unread: { color: 'warning' as const },
      read: { color: 'success' as const },
      archived: { color: 'default' as const },
      deleted: { color: 'error' as const },
      draft: { color: 'info' as const }
    };

    return (
      <Box display="flex" gap={1}>
        <Chip 
          label={typeConfig[type]?.label || type} 
          color={typeConfig[type]?.color || 'default'} 
          size="small" 
        />
        <Chip 
          label={status} 
          color={statusConfig[status]?.color || 'default'} 
          size="small" 
        />
      </Box>
    );
  };

  const formatEmailDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getUnreadCount = () => {
    return emails.filter(email => 
      email.direction === 'inbound' && email.status === 'unread'
    ).length;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <EmailIcon color="primary" />
            <Typography variant="h6">E-Mail Integration</Typography>
          </Box>
          <LinearProgress />
        </CardContent>
      </Card>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={2}>
              <EmailIcon color="primary" />
              <Typography variant="h6">E-Mail Integration</Typography>
              {getUnreadCount() > 0 && (
                <Badge badgeContent={getUnreadCount()} color="error">
                  <DraftsIcon />
                </Badge>
              )}
            </Box>
            <Box display="flex" gap={1}>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setComposeDialogOpen(true)}
                variant="contained"
                color="primary"
              >
                Neue E-Mail
              </Button>
              <IconButton 
                onClick={loadCaseEmails}
                disabled={loading}
                title="E-Mails aktualisieren"
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Enhanced Accordion Panels for Customer Cases */}
      {caseType === 'customer_order' && (
        <Box sx={{ mb: 3 }}>
          {/* Customer Decision Panel */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <CheckCircleIcon color={customerDecision.decision === 'won' ? 'success' : customerDecision.decision === 'lost' ? 'error' : 'disabled'} />
                <Typography variant="h6">Kundenentscheidung</Typography>
                {customerDecision.decision && (
                  <Chip 
                    label={customerDecision.decision === 'won' ? 'Gewonnen' : 'Verloren'}
                    color={customerDecision.decision === 'won' ? 'success' : 'error'}
                    size="small"
                  />
                )}
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Fall Status
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Typography variant="body2">
                          <strong>E-Mail Status:</strong> {
                            emailStatus?.email_sent 
                              ? `✅ Gesendet an ${emailStatus.recipient}` 
                              : '⏳ Noch nicht gesendet'
                          }
                        </Typography>
                        {emailStatus?.sent_at && (
                          <Typography variant="body2">
                            <strong>Gesendet am:</strong> {new Date(emailStatus.sent_at).toLocaleDateString('de-DE')}
                          </Typography>
                        )}
                        <Typography variant="body2">
                          <strong>Entscheidung:</strong> {
                            customerDecision.decision 
                              ? customerDecision.decision === 'won' ? '✅ Gewonnen' : '❌ Verloren'
                              : '⏳ Pending customer response'
                          }
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="subtitle2" gutterBottom>
                        Kundenentscheidung
                      </Typography>
                      <Box display="flex" flexDirection="column" gap={1}>
                        <Button
                          variant="contained"
                          startIcon={<CheckCircleIcon />}
                          color="success"
                          disabled={customerDecision.decision === 'won'}
                          fullWidth
                          onClick={() => setDecisionDialogOpen('won')}
                        >
                          Fall als Gewonnen markieren
                        </Button>
                        <Button
                          variant="outlined"
                          startIcon={<CancelIcon />}
                          color="error"
                          disabled={customerDecision.decision === 'lost'}
                          fullWidth
                          onClick={() => setDecisionDialogOpen('lost')}
                        >
                          Fall als Verloren markieren
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>

          {/* Supplier Quote Selection Review Panel */}
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box display="flex" alignItems="center" gap={2}>
                <BusinessIcon color="primary" />
                <Typography variant="h6">Lieferantenangebot Auswahl Review</Typography>
                <Chip 
                  label="Position-by-Position Review"
                  color="primary"
                  size="small"
                />
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Alert severity="info" sx={{ mb: 2 }}>
                <strong>Review Selected Supplier Quotes</strong><br />
                Review the supplier quotes selected for each customer quote position before making the final customer decision.
                This ensures proper coordination of supplier orders or cancellations based on Won/Lost outcome.
              </Alert>
              
              {/* Position-by-Position Quote Selection Review */}
              <Box sx={{ mt: 2 }}>
                {selectedCase?.metadata?.positions ? (
                  <Grid container spacing={2}>
                    {selectedCase.metadata.positions.map((position: any, index: number) => (
                      <Grid item xs={12} key={index}>
                        <Card variant="outlined" sx={{ border: '1px solid', borderColor: 'primary.light' }}>
                          <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                              <Typography variant="subtitle1" fontWeight="bold">
                                Position {index + 1}: {position.part_name || 'Material'}
                              </Typography>
                              <Chip 
                                label={position.supplier_selected ? 'Supplier Selected' : 'Pending Selection'}
                                color={position.supplier_selected ? 'success' : 'warning'}
                                size="small"
                              />
                            </Box>
                            
                            <Grid container spacing={2}>
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  <strong>Position Details:</strong>
                                </Typography>
                                <Box display="flex" flexDirection="column" gap={0.5}>
                                  <Typography variant="body2">
                                    • Menge: {position.quantity || 'N/A'}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Material: {position.type_specific_data?.material || 'Nicht spezifiziert'}
                                  </Typography>
                                  <Typography variant="body2">
                                    • Durchmesser: {position.type_specific_data?.diameter || 'N/A'}mm
                                  </Typography>
                                </Box>
                              </Grid>
                              
                              <Grid item xs={12} md={6}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                  <strong>Selected Supplier Quote:</strong>
                                </Typography>
                                {position.selected_supplier_quote ? (
                                  <Box display="flex" flexDirection="column" gap={0.5}>
                                    <Typography variant="body2">
                                      <strong>Lieferant:</strong> {position.selected_supplier_quote.supplier_name}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Preis:</strong> €{position.selected_supplier_quote.total_price?.toFixed(2) || 'N/A'}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Lieferzeit:</strong> {position.selected_supplier_quote.delivery_days || 'N/A'} Tage
                                    </Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      Auswahl Begründung: {position.selected_supplier_quote.selection_rationale || 'Keine Begründung'}
                                    </Typography>
                                  </Box>
                                ) : (
                                  <Typography variant="body2" color="text.secondary">
                                    📋 Noch keine Lieferantenauswahl getroffen
                                  </Typography>
                                )}
                              </Grid>
                            </Grid>
                            
                            {/* Action Button for Position */}
                            <Box sx={{ mt: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                              <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="caption" color="text.secondary">
                                  <strong>Coordination Status:</strong> {
                                    position.supplier_selected ? 
                                      'Ready for customer decision coordination' : 
                                      'Selection required before customer decision'
                                  }
                                </Typography>
                                <Button
                                  size="small"
                                  variant="outlined"
                                  onClick={() => {
                                    // Navigate to Select & Compile tab to make selection
                                    showNotification('Verwenden Sie den "Select & Compile" Tab um Lieferanten auszuwählen', 'info');
                                  }}
                                  disabled={position.supplier_selected}
                                >
                                  {position.supplier_selected ? 'Ausgewählt' : 'Auswählen'}
                                </Button>
                              </Box>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                ) : (
                  <Box sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50', borderRadius: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      📋 Keine Positionen verfügbar für Lieferantenauswahl Review
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Positionen werden beim Erstellen des Kundenangebots automatisch geladen
                    </Typography>
                  </Box>
                )}
                
                {/* Summary Section */}
                {selectedCase?.metadata?.positions && selectedCase.metadata.positions.length > 0 && (
                  <Card sx={{ mt: 3, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        Supplier Selection Summary
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Total Positions:</strong> {selectedCase.metadata.positions.length}
                          </Typography>
                        </Grid>
                        <Grid item xs={6}>
                          <Typography variant="body2">
                            <strong>Selected:</strong> {
                              selectedCase.metadata.positions.filter((p: any) => p.supplier_selected).length
                            }
                          </Typography>
                        </Grid>
                      </Grid>
                      
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="caption">
                          <strong>Ready for Customer Decision:</strong> {
                            selectedCase.metadata.positions.every((p: any) => p.supplier_selected) 
                              ? '✅ All suppliers selected - ready for Won/Lost decision'
                              : '⚠️ Complete supplier selection before customer decision'
                          }
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {/* Email List */}
      {emails.length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          Keine E-Mails für diesen Fall gefunden. Erstellen Sie eine neue E-Mail, um die Kommunikation zu beginnen.
        </Alert>
      ) : (
        <Box>
          {emails.map((email) => {
            const isUnread = email.direction === 'inbound' && email.status === 'unread';
            const CardComponent = isUnread ? UnreadEmailCard : EmailCard;
            
            return (
              <CardComponent key={email.id}>
                <CardContent>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={8}>
                      <Typography variant="h6" gutterBottom>
                        {email.subject}
                      </Typography>
                      <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Avatar sx={{ width: 32, height: 32 }}>
                          {email.direction === 'inbound' ? <PersonIcon /> : <BusinessIcon />}
                        </Avatar>
                        <Box>
                          <Typography variant="body2">
                            {email.direction === 'inbound' 
                              ? `Von: ${email.from_address}`
                              : `An: ${Array.isArray(email.to_addresses) 
                                      ? email.to_addresses.join(', ') 
                                      : email.to_addresses}`
                            }
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {formatEmailDate(email.email_date || email.sent_date || email.created_at)}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                        {getEmailTypeChip(email.direction, email.status)}
                      </Box>
                    </Grid>
                  </Grid>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Typography variant="body2" sx={{ mb: 2 }}>
                    {(email.body_text || email.body_html)?.substring(0, 200)}
                    {((email.body_text || email.body_html) || '')?.length > 200 && '...'}
                  </Typography>
                  
                  <Box display="flex" justifyContent="flex-end" gap={1}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => {
                        setSelectedEmail(email);
                        setEmailDetailDialogOpen(true);
                      }}
                    >
                      Details
                    </Button>
                    {email.direction === 'inbound' && (
                      <Button
                        size="small"
                        startIcon={<ReplyIcon />}
                        onClick={() => {
                          setSelectedEmail(email);
                          setReplyEmail({
                            subject: `Re: ${email.subject}`,
                            body: `\n\n--- Original Message ---\nVon: ${email.from_address}\nGesendet: ${formatEmailDate(email.email_date || email.sent_date || email.created_at)}\nBetreff: ${email.subject}\n\n${email.body_text || email.body_html}`
                          });
                          setReplyDialogOpen(true);
                        }}
                        variant="outlined"
                      >
                        Antworten
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </CardComponent>
            );
          })}
        </Box>
      )}

      {/* Compose Email Dialog */}
      <Dialog
        open={composeDialogOpen}
        onClose={() => setComposeDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <SendIcon color="primary" />
            Neue E-Mail erstellen
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="An *"
                value={newEmail.to}
                onChange={(e) => setNewEmail({ ...newEmail, to: e.target.value })}
                placeholder="empfaenger@example.com"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CC"
                value={newEmail.cc}
                onChange={(e) => setNewEmail({ ...newEmail, cc: e.target.value })}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="BCC"
                value={newEmail.bcc}
                onChange={(e) => setNewEmail({ ...newEmail, bcc: e.target.value })}
                placeholder="Optional"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Betreff *"
                value={newEmail.subject}
                onChange={(e) => setNewEmail({ ...newEmail, subject: e.target.value })}
                placeholder="E-Mail Betreff"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={10}
                label="Nachricht *"
                value={newEmail.body}
                onChange={(e) => setNewEmail({ ...newEmail, body: e.target.value })}
                placeholder="Ihre Nachricht..."
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleSendEmail}
            disabled={sendingEmail}
            variant="contained"
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <SendIcon />}
          >
            {sendingEmail ? 'Wird gesendet...' : 'E-Mail senden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reply Email Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={() => setReplyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            <ReplyIcon color="primary" />
            Auf E-Mail antworten
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedEmail && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  Antwort an: {selectedEmail.from_address}
                </Alert>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Betreff"
                  value={replyEmail.subject}
                  onChange={(e) => setReplyEmail({ ...replyEmail, subject: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  multiline
                  rows={10}
                  label="Antwort *"
                  value={replyEmail.body}
                  onChange={(e) => setReplyEmail({ ...replyEmail, body: e.target.value })}
                  placeholder="Ihre Antwort..."
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>
            Abbrechen
          </Button>
          <Button
            onClick={handleReplyToEmail}
            disabled={sendingEmail}
            variant="contained"
            startIcon={sendingEmail ? <CircularProgress size={20} /> : <ReplyIcon />}
          >
            {sendingEmail ? 'Wird gesendet...' : 'Antwort senden'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Email Detail Dialog using EmailDetail Component */}
      {selectedEmail && (
        <Dialog
          open={emailDetailDialogOpen}
          onClose={() => {
            setEmailDetailDialogOpen(false);
            setSelectedEmail(null);
          }}
          maxWidth="lg"
          fullWidth
        >
          <EmailDetail
            email={{
              ...selectedEmail,
              to_addresses: Array.isArray(selectedEmail.to_addresses) 
                ? selectedEmail.to_addresses 
                : [selectedEmail.to_addresses],
              cc_addresses: selectedEmail.cc_addresses 
                ? (Array.isArray(selectedEmail.cc_addresses) 
                   ? selectedEmail.cc_addresses 
                   : [selectedEmail.cc_addresses])
                : [],
              bcc_addresses: selectedEmail.bcc_addresses 
                ? (Array.isArray(selectedEmail.bcc_addresses) 
                   ? selectedEmail.bcc_addresses 
                   : [selectedEmail.bcc_addresses])
                : []
            } as any}
            onEmailUpdate={handleEmailUpdate}
            onClose={() => {
              setEmailDetailDialogOpen(false);
              setSelectedEmail(null);
            }}
            onReply={(emailId) => {
              const email = emails.find(e => e.id === emailId);
              if (email) {
                setSelectedEmail(email);
                setReplyEmail({
                  subject: `Re: ${email.subject}`,
                  body: `\n\n--- Original Message ---\nVon: ${email.from_address}\nGesendet: ${formatEmailDate(email.email_date || email.sent_date || email.created_at)}\nBetreff: ${email.subject}\n\n${email.body_text || email.body_html}`
                });
                setEmailDetailDialogOpen(false);
                setReplyDialogOpen(true);
              }
            }}
            onForward={(emailId) => {
              // Handle forward if needed
              showNotification('Forward functionality not implemented yet', 'info');
            }}
          />
        </Dialog>
      )}

      {/* Floating Action Button for Quick Email */}
      <Fab
        color="primary"
        sx={{ position: 'fixed', bottom: 16, right: 16 }}
        onClick={() => setComposeDialogOpen(true)}
      >
        <AddIcon />
      </Fab>

      {/* Customer Decision Dialog */}
      <Dialog
        open={decisionDialogOpen !== null}
        onClose={() => setDecisionDialogOpen(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center" gap={2}>
            {decisionDialogOpen === 'won' ? (
              <CheckCircleIcon color="success" />
            ) : (
              <CancelIcon color="error" />
            )}
            <Typography variant="h6">
              Fall als {decisionDialogOpen === 'won' ? 'Gewonnen' : 'Verloren'} markieren
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} sx={{ mt: 1 }}>
            <TextField
              label="Entscheidungsnotizen"
              multiline
              rows={3}
              value={customerDecision.decision_notes}
              onChange={(e) => setCustomerDecision({
                ...customerDecision,
                decision_notes: e.target.value
              })}
              fullWidth
            />
            <TextField
              label="Kundenfeedback"
              multiline
              rows={2}
              value={customerDecision.customer_feedback}
              onChange={(e) => setCustomerDecision({
                ...customerDecision,
                customer_feedback: e.target.value
              })}
              fullWidth
            />
            {decisionDialogOpen === 'won' && (
              <>
                <TextField
                  label="Auftragswert (Optional)"
                  value={customerDecision.decision_amount}
                  onChange={(e) => setCustomerDecision({
                    ...customerDecision,
                    decision_amount: e.target.value
                  })}
                  fullWidth
                />
                <TextField
                  label="Erwartetes Auftragsdatum"
                  type="date"
                  value={customerDecision.expected_order_date}
                  onChange={(e) => setCustomerDecision({
                    ...customerDecision,
                    expected_order_date: e.target.value
                  })}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                />
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDecisionDialogOpen(null)}>
            Abbrechen
          </Button>
          <Button
            onClick={() => decisionDialogOpen && handleRecordDecision(decisionDialogOpen)}
            variant="contained"
            color={decisionDialogOpen === 'won' ? 'success' : 'error'}
          >
            {decisionDialogOpen === 'won' ? 'Als Gewonnen markieren' : 'Als Verloren markieren'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default EmailIntegrationPanel;
